import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Filter, SearchService } from '@docpath/linces-angular-components/common';
import { DpFormModule, DpHeaderModule, DpSearchConfig, DpSearchModule, DpSection, DpSectionIcon, HeaderAction, MoreMenuGroup, SearchBasicInfo, SearchMode, SearchOptionType, SearchRowInfo, SearchRowInfoSelect } from '@docpath/webcomponents';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Constants } from 'src/app/constants/constants';
import { PageHeaderActionsService } from 'src/app/shared/components/header/services/page-header-actions.service';

import { Dge, DgeState } from '../../models/dge';
import { EnginesService } from '../../services/engines.service';
import { EnginesTableService } from '../table/engines-table.service';
import { EnginesHeaderService } from './engines-header.service';

@Component({
    selector: 'app-engines-header',
    templateUrl: './engines-header.component.html',
    standalone: true,
    imports: [DpFormModule, DpHeaderModule, DpSearchModule]
})
export class EnginesHeaderComponent implements OnChanges, OnInit {
    @Input() dges: Dge[] = [];
    @Output() filtersChanged = new EventEmitter<Filter[]>();

    section = new DpSection({ name: 'engines', rows: [], header: 'engines-config.header.title', tooltip: 'engines-config.header.title-tooltip', icon: new DpSectionIcon(faGear) });
    rightActions: HeaderAction[] = [];
    leftActions: HeaderAction[] = [];
    moreMenuGroups: MoreMenuGroup[] = [];
    showSearch = false;
    searchConfig = new DpSearchConfig({ resetOnClose: true, showMinimizeButton: true });
    private filters: Filter[] = [];
    private enginesChangedSubscription?: Subscription;
    private translateSubscription?: Subscription;
    searchAvailableOptions: SearchRowInfo[] = [
        new SearchRowInfo(true, Constants.CONF_ENGINES_ENGINE_NAME, undefined, SearchOptionType.TEXT_FULL, SearchMode.exactly, true, true),
        new SearchRowInfo(false, Constants.CONF_ENGINES_URL, undefined, SearchOptionType.TEXT_FULL, SearchMode.exactly, true, true),
        new SearchRowInfoSelect(false, Constants.CONF_ENGINES_STATUS, undefined, SearchMode.is, this.getStatusFilterOptions(), true, true),
        new SearchRowInfo(false, Constants.CONF_ENGINES_SINCLAIR_INDEX, undefined, SearchOptionType.TEXT_FULL, SearchMode.exactly, true, true)
    ];

    private readonly enginesHeaderService = inject(EnginesHeaderService);
    private readonly enginesTableService = inject(EnginesTableService);
    private readonly enginesService = inject(EnginesService);
    private readonly searchService = inject(SearchService);
    private readonly translateService = inject(TranslateService);
    private readonly pageHeaderActionsService = inject(PageHeaderActionsService);

    constructor() {
        this.rightActions = this.enginesHeaderService.getRightActions();
        const searchAction = this.enginesHeaderService.getSearchAction(this.rightActions);
        searchAction.action = () => {
            this.showSearch = !this.showSearch;
            this.updateSearchStyle();
        };
    }

    ngOnInit(): void {

        if (!this.enginesChangedSubscription) {
            this.enginesChangedSubscription = this.enginesService.engineChanged.subscribe(() => {
                this.moreMenuGroups = [];
            });
        }

        if (!this.translateSubscription) {
            this.translateSubscription = this.translateService.onLangChange.subscribe(() => {
                this.updateSearchStyle();
            });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line dot-notation
        if (changes['dges']) {
            const moreMenuGroupsActions = this.enginesHeaderService.getMoreActions(this.dges);
            this.moreMenuGroups = moreMenuGroupsActions.length > 0 ? [{ actions: moreMenuGroupsActions }] : [];
        }
    }

    getStatusFilterOptions(): Map<string, string> {
        return new Map<string, string>([
            [DgeState.RUNNING, DgeState.RUNNING],
            [DgeState.STOPPING, DgeState.STOPPING],
            [DgeState.STOPPED, DgeState.STOPPED],
            [DgeState.INTERRUPTED, DgeState.INTERRUPTED],
            [DgeState.UNLINKED, DgeState.UNLINKED]
        ]);
    }

    isSearchVisible(): boolean {
        return this.showSearch;
    }

    clearSearch(): void {
        this.filters = [];
        this.filtersChanged.emit(this.filters);
        this.enginesTableService.loadDges(this.filters, 1);
        this.moreMenuGroups = [];
        this.updateSearchStyle(0);
    }

    sendSearch(currentFilters: Array<SearchBasicInfo>): void {
        this.filters = this.searchService.toFilters(currentFilters);
        this.filtersChanged.emit(this.filters);
        this.enginesTableService.loadDges(this.filters, 1);
        this.moreMenuGroups = [];
        this.updateSearchStyle();
    }

    hideSearch(): void {
        this.showSearch = false;
    }

    private updateSearchStyle(filtersCount?: number): void {
        const filterKeys = new Set(this.filters.map(filter => filter.key)),
            count = filtersCount ?? filterKeys.size;

        this.pageHeaderActionsService.updateSearchStyle(this.rightActions, count, 'search');
    }

}